import memoize from 'lib/memoize/memoize'
import type {
  TripItemMapMarker,
} from 'tripPlanner/components/TripMaps/utils'
import type {
  ExploreItem,
  FullTrip,
  GeoPlace,
  ItemWithOnlyLatLng,
  Place,
} from 'tripPlanner/types/common'
import type { PublicTripItem } from 'tripPlanner/types/publicTrip'
import type {
  AttractionItem,
  RestaurantBarItem,
  TripItem,
} from 'tripPlanner/types/tripItem'

export function isExploreItem(
  item: ExploreItem | TripItem | ItemWithOnlyLatLng,
): item is ExploreItem {
  return 'types' in item
}

// Use this if you don't want to show the same place twice on the map
// Filter out saved items that have the same place as one of the explore items
export function filterSavedItemsWithExplorePlaces(
  mapMarkers: Array<TripItemMapMarker>,
  exploreItems: Array<ExploreItem>,
): Array<TripItemMapMarker> {
  const exploreItemsIds = new Set(
    exploreItems.map((exploreItem) => exploreItem.id),
  )

  return mapMarkers.filter(
    (mapMarker) =>
      !(
        'place' in mapMarker.item &&
        exploreItemsIds.has(mapMarker.item.place.id!)
      ),
  )
}

export function getMatchingSavedExploreItem(
  trip: FullTrip,
  exploreItem: ExploreItem,
): AttractionItem | RestaurantBarItem | undefined {
  return getPlaceIdToSavedExploreItemMap(trip.items).get(
    exploreItem.place.id ?? '',
  ) as AttractionItem | RestaurantBarItem | undefined
}

type SavedItem = (TripItem | PublicTripItem) & { place: any }

export function getMatchingSavedItem(
  trip: FullTrip,
  exploreItemId: string = '',
): SavedItem | undefined {
  return getPlaceIdToSavedItemMap(trip.items).get(exploreItemId)
}

export const getPlaceIdToSavedItemMap = memoize(
  (items: Array<TripItem | PublicTripItem>) => {
    const map = new Map<string, SavedItem>()

    items.forEach((item) => {
      if ('place' in item && item.place.type === 'GEO' && item.place.id) {
        map.set(item.place.id, item)
      }
    })

    return map
  },
)

export const getPlaceIdToSavedExploreItemMap = memoize(
  (items: Array<TripItem | PublicTripItem>) => {
    const map = new Map<string, SavedItem & { place: Place }>()

    items.forEach((item) => {
      if (
        'place' in item &&
        item.place.type === 'GEO' &&
        item.sourceType === 'Explore'
      ) {
        map.set(item.place.id!, item)
      }
    })

    return map
  },
)

export const getPlaceIdToExploreItemMap = memoize(
  (items: Array<ExploreItem>) => {
    const map = new Map<string, ExploreItem & { place: GeoPlace }>()

    items.forEach((item) => {
      map.set(item.place.id!, item)
    })

    return map
  },
)

export function cleanUrlString(urlString: string): string {
  // Remove 'https://' or 'http://' if present & utm params
  const cleanedUrl = urlString.replace(
    /^(https?:\/\/)?(www\.)?|(\/)?(\?|&)utm_[^&#]+|\/.*$/g,
    '',
  )
  return cleanedUrl
}
